import axios, { AxiosResponse } from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Reject } from '../Reject';
import { logout } from './auth';
import { Profile, Spot } from '../../domain';
import { handleApiError } from '../handleApiError';

interface PublicProfileState {
    getPublicProfilePending: boolean;
    getPublicProfileSuccess: boolean;
    getPublicProfileError: boolean;
    getPublicProfileData: Profile;
    getPublicSpotsByUserPending: boolean;
    getPublicSpotsByUserSuccess: boolean;
    getPublicSpotsByUserError: boolean;
    getPublicSpotsByUserData: Spot[];
}

const initialState: PublicProfileState = {
    getPublicProfilePending: false,
    getPublicProfileSuccess: false,
    getPublicProfileError: false,
    getPublicProfileData: undefined,
    getPublicSpotsByUserPending: false,
    getPublicSpotsByUserSuccess: false,
    getPublicSpotsByUserError: true,
    getPublicSpotsByUserData: undefined,
};

interface GetPublicProfileParams {
    idUser: string;
}

export const getPublicProfile = createAsyncThunk<Profile, GetPublicProfileParams, Reject>( //todo
    'getProfilePublic',
    (params, thunkAPI) => {
        const { idUser } = params;
        return axios
            .get(`${process.env.REACT_APP_API_URL}/user/publicProfile/${idUser}`, {
                headers: {
                    'Content-Type': 'application/json',
                },
            })
            .then((response: AxiosResponse<Profile>) => response.data)
            .catch((error) => {
                const apiError = handleApiError(error);
                return thunkAPI.rejectWithValue(apiError);
            });
    }
);

interface GetPublicSpotsByUserParams {
    idUser: string;
}

export const getPublicSpotsByUser = createAsyncThunk<Spot[], GetPublicSpotsByUserParams, Reject>(
    'getPublicSpotsByUser',
    (params, thunkAPI) => {
        const { idUser } = params;
        return axios
            .get(`${process.env.REACT_APP_API_URL}/user/publicSpots/${idUser}`, {
                headers: {
                    'Content-Type': 'application/json',
                },
            })
            .then((response: AxiosResponse<Spot[]>) => response.data)
            .catch((error) => {
                const apiError = handleApiError(error);
                return thunkAPI.rejectWithValue(apiError);
            });
    }
);

const publicProfileSlice = createSlice({
    name: 'publicProfile',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getPublicProfile.pending, (state) => {
                state.getPublicProfilePending = true;
                state.getPublicProfileSuccess = false;
                state.getPublicProfileError = false;
                delete state.getPublicProfileData;
            })
            .addCase(getPublicProfile.rejected, (state) => {
                state.getPublicProfilePending = false;
                state.getPublicProfileSuccess = false;
                state.getPublicProfileError = true;
                delete state.getPublicProfileData;
            })
            .addCase(getPublicProfile.fulfilled, (state, action) => {
                state.getPublicProfilePending = false;
                state.getPublicProfileSuccess = true;
                state.getPublicProfileError = false;
                state.getPublicProfileData = action.payload;
            })
            .addCase(getPublicSpotsByUser.pending, (state) => {
                state.getPublicSpotsByUserPending = true;
                state.getPublicSpotsByUserSuccess = false;
                state.getPublicSpotsByUserError = false;
                delete state.getPublicSpotsByUserData;
            })
            .addCase(getPublicSpotsByUser.rejected, (state) => {
                state.getPublicSpotsByUserPending = false;
                state.getPublicSpotsByUserSuccess = false;
                state.getPublicSpotsByUserError = true;
                delete state.getPublicSpotsByUserData;
            })
            .addCase(getPublicSpotsByUser.fulfilled, (state, action) => {
                state.getPublicSpotsByUserPending = false;
                state.getPublicSpotsByUserSuccess = true;
                state.getPublicSpotsByUserError = false;
                state.getPublicSpotsByUserData = action.payload;
            })
            .addCase(logout.toString(), () => ({ ...initialState }));
    },
});

export default publicProfileSlice.reducer;
